"use client"

import { Dialog, Transition } from "@headlessui/react"
import Image from "next/image"
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { API_BASE_URL } from "@lib/config"
import { LuDownload, LuLoader, LuX } from "react-icons/lu"
import { CiCamera } from "react-icons/ci"
import { readJsonResponse } from "@lib/http/read-json-response"
import {
  MAX_SESSION_UPLOAD_ATTEMPTS,
  type SessionUploadResult,
  type SessionUploadState,
  runSessionPhotoUploadWithRetry,
  validateVirtualTryOnImageFile,
  VIRTUAL_TRY_ON_FILE_ACCEPT_ATTRIBUTE,
} from "./session-upload"

type VirtualTryOnDrawerProps = {
  open: boolean
  onClose: () => void
  product: any
}

type Status =
  | "idle"
  | "ready"
  | "uploading_or_generating"
  | "success"
  | "error_retry"

const buildRandomHexId = (byteLength: number): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    const bytes = new Uint8Array(byteLength)
    crypto.getRandomValues(bytes)
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      ""
    )
  }

  let fallback = ""
  while (fallback.length < byteLength * 2) {
    fallback += Math.random().toString(16).slice(2)
  }

  return fallback.slice(0, byteLength * 2)
}

const VirtualTryOnDrawer = ({
  open,
  onClose,
  product,
}: VirtualTryOnDrawerProps) => {
  const [status, setStatus] = useState<Status>("idle")
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  )
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [inputKey, setInputKey] = useState(0)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const cameraInputRef = useRef<HTMLInputElement | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const sessionUploadAbortControllerRef = useRef<AbortController | null>(null)
  const sessionUploadRequestIdRef = useRef(0)
  const previewUrlRef = useRef<string | null>(null)
  const [previewUrl, setPreviewUrlState] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [sessionUploadState, setSessionUploadState] =
    useState<SessionUploadState>("idle")
  const [latestSessionUploadId, setLatestSessionUploadId] = useState<
    string | null
  >(null)
  const [hasUploadedSelectedFile, setHasUploadedSelectedFile] = useState(false)
  const [pendingGenerate, setPendingGenerate] = useState(false)

  const setPreviewUrl = useCallback((next: string | null) => {
    setPreviewUrlState((prev) => {
      if (prev && prev !== next) {
        URL.revokeObjectURL(prev)
      }
      previewUrlRef.current = next
      return next
    })
  }, [])

  useEffect(() => {
    return () => {
      const existing = previewUrlRef.current
      if (existing) {
        URL.revokeObjectURL(existing)
      }
    }
  }, [])

  const heroImage = useMemo(() => {
    if (!Array.isArray(product?.images) || product.images.length === 0) {
      return null
    }
    return product.images[0]?.url || null
  }, [product])

  const createSessionId = useCallback(() => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID().replace(/-/g, "")
    }

    return buildRandomHexId(16)
  }, [])

  const ensureSessionId = useCallback(() => {
    if (sessionId) {
      return sessionId
    }
    const nextSessionId = createSessionId()
    setSessionId(nextSessionId)
    return nextSessionId
  }, [createSessionId, sessionId])

  const cancelSessionUpload = useCallback(() => {
    if (sessionUploadAbortControllerRef.current) {
      sessionUploadAbortControllerRef.current.abort()
      sessionUploadAbortControllerRef.current = null
    }
    sessionUploadRequestIdRef.current += 1
  }, [])

  const resetFlow = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    cancelSessionUpload()
    setValidationMessage(null)
    setSelectedFile(null)
    setResultUrl(null)
    setPreviewUrl(null)
    setSessionUploadState("idle")
    setLatestSessionUploadId(null)
    setHasUploadedSelectedFile(false)
    setPendingGenerate(false)
    setDragActive(false)
    setStatus("idle")
    setInputKey((prev) => prev + 1)
  }, [cancelSessionUpload, setPreviewUrl])

  const resetDrawerState = useCallback(() => {
    resetFlow()
    setSessionId(null)
  }, [resetFlow])

  useEffect(() => {
    if (!open) {
      resetDrawerState()
      return
    }

    setSessionId(createSessionId())
  }, [createSessionId, open, resetDrawerState])

  const uploadSessionPhotoInBackground = useCallback(
    (file: File): Promise<SessionUploadResult> => {
      const activeSessionId = ensureSessionId()
      cancelSessionUpload()
      setHasUploadedSelectedFile(false)
      setLatestSessionUploadId(null)
      const requestId = sessionUploadRequestIdRef.current
      const isCurrentRequest = () =>
        sessionUploadRequestIdRef.current === requestId

      return runSessionPhotoUploadWithRetry({
        maxAttempts: MAX_SESSION_UPLOAD_ATTEMPTS,
        shouldContinue: isCurrentRequest,
        onStateChange: (nextState) => {
          if (isCurrentRequest()) {
            setSessionUploadState(nextState)
          }
        },
        uploadOnce: async () => {
          if (!isCurrentRequest()) {
            throw new DOMException("Outdated session upload", "AbortError")
          }

          const controller = new AbortController()
          sessionUploadAbortControllerRef.current = controller
          try {
            const formData = new FormData()
            formData.append("photo", file)

            const response = await fetch(
              `${API_BASE_URL}/public/virtual-try-on/sessions/${encodeURIComponent(
                activeSessionId
              )}/photo`,
              {
                method: "POST",
                body: formData,
                signal: controller.signal,
              }
            )
            const payload = await readJsonResponse<{
              error?: string
              uploadId?: string
            }>(response, {
              context: "virtual try-on photo upload",
              optional: true,
            })
            if (!response.ok) {
              const reason =
                payload && typeof payload.error === "string"
                  ? payload.error
                  : "Session photo upload failed"
              throw new Error(reason)
            }

            if (isCurrentRequest()) {
              setLatestSessionUploadId(
                payload && typeof payload.uploadId === "string"
                  ? payload.uploadId
                  : null
              )
              setHasUploadedSelectedFile(true)
            }
          } finally {
            if (sessionUploadAbortControllerRef.current === controller) {
              sessionUploadAbortControllerRef.current = null
            }
          }
        },
      }).then((result) => {
        if (result === "aborted" && isCurrentRequest()) {
          setSessionUploadState("idle")
        }
        return result
      })
    },
    [cancelSessionUpload, ensureSessionId]
  )

  const handleFileSelection = useCallback(
    (file: File | null) => {
      if (!file) return
      const validation = validateVirtualTryOnImageFile(file)
      if (validation.ok === false) {
        setValidationMessage(validation.error)
        return
      }

      setValidationMessage(null)
      setSelectedFile(file)
      const nextPreview = URL.createObjectURL(file)
      setPreviewUrl(nextPreview)
      setLatestSessionUploadId(null)
      setHasUploadedSelectedFile(false)
      setStatus((prev) => (prev === "error_retry" ? "error_retry" : "ready"))
      void uploadSessionPhotoInBackground(file)
    },
    [setPreviewUrl, uploadSessionPhotoInBackground]
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null
      if (event.target) {
        event.target.value = ""
      }
      handleFileSelection(file)
    },
    [handleFileSelection]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (status === "uploading_or_generating") {
        return
      }
      setDragActive(false)
      const file = event.dataTransfer?.files?.[0] || null
      handleFileSelection(file)
    },
    [handleFileSelection, status]
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (status !== "uploading_or_generating") {
        setDragActive(true)
      }
    },
    [status]
  )

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      setDragActive(false)
    },
    []
  )

  const executeGenerate = useCallback(
    async (activeSessionId: string) => {
      setValidationMessage(null)
      setStatus("uploading_or_generating")

      const controller = new AbortController()
      abortControllerRef.current = controller
      try {
        const formData = new FormData()
        formData.append("sessionId", activeSessionId)

        const productId = product?._id || product?.id
        const response = await fetch(
          `${API_BASE_URL}/public/products/${encodeURIComponent(
            String(productId)
          )}/virtual-try-on`,
          {
            method: "POST",
            body: formData,
            signal: controller.signal,
          }
        )
        const data = await readJsonResponse<{ resultImageUrl?: string }>(
          response,
          { context: "virtual try-on", optional: true }
        )
        if (!response.ok || !data || typeof data.resultImageUrl !== "string") {
          throw new Error("Virtual try-on failed")
        }

        setResultUrl(data.resultImageUrl)
        setPendingGenerate(false)
        setStatus("success")
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          return
        }
        // console.error("Virtual try-on generation failed", err)
        setResultUrl(null)
        setSelectedFile(null)
        setPreviewUrl(null)
        setSessionUploadState("idle")
        setLatestSessionUploadId(null)
        setHasUploadedSelectedFile(false)
        setPendingGenerate(false)
        setInputKey((prev) => prev + 1)
        setStatus("error_retry")
      } finally {
        abortControllerRef.current = null
      }
    },
    [product, setPreviewUrl]
  )

  const handleGenerate = useCallback(() => {
    if (!selectedFile) {
      setValidationMessage("Subí una foto para continuar.")
      return
    }
    if (status === "uploading_or_generating") {
      return
    }

    setValidationMessage(null)
    const activeSessionId = ensureSessionId()

    if (
      hasUploadedSelectedFile &&
      sessionUploadState === "uploaded" &&
      latestSessionUploadId
    ) {
      setPendingGenerate(false)
      void executeGenerate(activeSessionId)
      return
    }

    setPendingGenerate(true)

    if (
      sessionUploadState === "uploading" ||
      sessionUploadState === "retrying"
    ) {
      return
    }

    void uploadSessionPhotoInBackground(selectedFile)
  }, [
    ensureSessionId,
    executeGenerate,
    hasUploadedSelectedFile,
    latestSessionUploadId,
    selectedFile,
    sessionUploadState,
    status,
    uploadSessionPhotoInBackground,
  ])

  useEffect(() => {
    if (!pendingGenerate) {
      return
    }
    if (!selectedFile || status === "uploading_or_generating") {
      return
    }

    if (
      hasUploadedSelectedFile &&
      sessionUploadState === "uploaded" &&
      latestSessionUploadId
    ) {
      const activeSessionId = ensureSessionId()
      setPendingGenerate(false)
      void executeGenerate(activeSessionId)
      return
    }

    if (sessionUploadState === "blocked") {
      setPendingGenerate(false)
    }
  }, [
    ensureSessionId,
    executeGenerate,
    hasUploadedSelectedFile,
    latestSessionUploadId,
    pendingGenerate,
    selectedFile,
    sessionUploadState,
    status,
  ])

  const handleReset = useCallback(() => {
    resetFlow()
  }, [resetFlow])

  const closeDrawer = useCallback(() => {
    onClose()
  }, [onClose])

  const handleCameraSelection = useCallback(() => {
    cameraInputRef.current?.click()
  }, [])

  const handleGallerySelection = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  /* ------------------------------------------------------------------ */
  /*  Render helpers                                                     */
  /* ------------------------------------------------------------------ */

  const renderStickyCTA = (label: string) => (
    <div className="sticky bottom-0 z-10 -mx-5 sm:-mx-8 mt-8 border-t border-black/5 bg-white px-5 sm:px-8 pt-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
      <div className="flex items-center gap-4">
        {heroImage && (
          <div className="relative h-16 w-12 flex-shrink-0 overflow-hidden bg-zinc-50">
            <Image
              src={heroImage}
              alt={product?.name ?? "Prenda"}
              fill
              className="object-cover"
              sizes="48px"
            />
          </div>
        )}
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!selectedFile}
          className="flex-1 bg-black py-3.5 text-[12px] font-lealti uppercase tracking-[0.2em] text-white transition hover:bg-black/90 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-400"
        >
          {label}
        </button>
      </div>
    </div>
  )

  const renderUploadStep = () => (
    <div
      className="space-y-4"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Preview area */}
      <div
        className={`relative overflow-hidden bg-zinc-50 aspect-[9/10] md:aspect-[9/11] ${
          dragActive ? "ring-2 ring-black/20" : ""
        } transition`}
      >
        {previewUrl ? (
          <>
            <Image
              src={previewUrl}
              alt="Vista previa de tu foto"
              fill
              className="object-cover"
              sizes="(max-width: 640px) calc(100vw - 40px), 576px"
            />
            <button
              type="button"
              onClick={handleReset}
              className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center bg-white/90 backdrop-blur-sm text-black transition hover:bg-white"
              aria-label="Quitar foto"
            >
              <LuX className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleGallerySelection}
            className="flex h-full w-full flex-col items-center justify-center gap-3"
          >
            <CiCamera className="h-8 w-8 text-black/20" />
            <span className="text-[11px] font-lealti uppercase tracking-[0.2em] text-black/40">
              Subí tu foto
            </span>
          </button>
        )}
      </div>
      <p className="text-[10px] font-lealti text-black/70">
        * Los archivos son eliminados automáticamente después de la generación
        de IA.
      </p>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleCameraSelection}
          className="border border-black py-3.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-black transition hover:bg-black hover:text-white"
        >
          Sacar foto
        </button>
        <button
          type="button"
          onClick={handleGallerySelection}
          className="bg-black py-3.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-white transition hover:bg-black/90"
        >
          Subir foto
        </button>
      </div>

      {/* File name */}
      {selectedFile && (
        <p className="text-[10px] font-lealti-light text-black/50 truncate">
          {selectedFile.name}
        </p>
      )}

      {/* Validation message */}
      {validationMessage && (
        <p className="text-[11px] font-lealti-light text-red-500">
          {validationMessage}
        </p>
      )}

      {/* Helper text */}
      <div className="flex flex-col">
        <p className="text-md font-bold text-black">Para mejores resultados</p>
        <p className="text-[12px] font-lealti text-black">
          - Foto de cuerpo entero utilizando ropa del color de tu tono de piel.
        </p>
        <p className="text-[12px] font-lealti text-black">
          - Buena iluminación y fondo simple, evita personas u objetos.
        </p>
      </div>
      {renderStickyCTA("Iniciar probador virtual")}
    </div>
  )

  const renderProcessingStep = () => (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
      <LuLoader className="h-5 w-5 animate-spin text-black" />
      <p className="text-[13px] font-lealti uppercase tracking-[0.2em] text-black">
        Generando tu look...
      </p>
      <p className="max-w-[280px] text-[11px] font-lealti-light text-black/40">
        Estamos combinando tu foto con {product?.name ?? "la prenda"}. Este
        proceso puede tardar unos segundos.
      </p>
      <div className="mt-2 h-[1px] w-24 overflow-hidden bg-black/10">
        <div className="h-full w-full animate-pulse bg-black" />
      </div>
    </div>
  )

  const renderResultStep = () => (
    <div className="space-y-6">
      <div className="relative overflow-hidden bg-zinc-50 aspect-[9/10] md:aspect-[9/11]">
        {resultUrl ? (
          <Image
            src={resultUrl}
            alt="Resultado del probador virtual"
            fill
            className="object-cover"
            sizes="(max-width: 640px) calc(100vw - 40px), 576px"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[11px] font-lealti-light text-black/40">
            No pudimos mostrar la imagen generada.
          </div>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center justify-center gap-2 border border-black py-3.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-black transition hover:bg-black hover:text-white"
        >
          Probar con otra foto
        </button>
        {resultUrl && (
          <a
            href={resultUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-black py-3.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-white transition hover:bg-black/90"
          >
            <LuDownload className="h-4 w-4" />
            Descargar imagen
          </a>
        )}
      </div>
    </div>
  )

  const renderRetryStep = () => (
    <div className="space-y-6">
      <div className="pt-5">
        <p className="text-[18px] font-bold  text-black">¡Ups!</p>
        <p className="text-[14px] font-lealti-light text-black">
          Por favor, intentá de nuevo con otra foto.
        </p>
        <p className="text-[13px] font-lealti-light text-black/70 mt-2">
          No fue posible generar el resultado con esa imagen. Probá con una foto
          de cuerpo completo, buena luz y fondo simple.
        </p>
      </div>
      <div className="flex flex-col">
        <p className="text-md font-bold text-black">Para mejores resultados</p>
        <p className="text-[12px] font-lealti text-black">
          - Foto de cuerpo entero utilizando ropa del color de tu tono de piel.
        </p>
        <p className="text-[12px] font-lealti text-black">
          - Buena iluminación y fondo simple, evita personas u objetos.
        </p>
      </div>
      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleCameraSelection}
          className="border border-black py-3.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-black transition hover:bg-black hover:text-white"
        >
          Sacar foto
        </button>
        <button
          type="button"
          onClick={handleGallerySelection}
          className="bg-black py-3.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-white transition hover:bg-black/90"
        >
          Subir foto
        </button>
      </div>
      {/* Preview */}
      <div className="relative overflow-hidden bg-zinc-50 aspect-[9/10] md:aspect-[9/11]">
        {previewUrl ? (
          <>
            <Image
              src={previewUrl}
              alt="Vista previa de tu foto"
              fill
              className="object-cover"
              sizes="(max-width: 640px) calc(100vw - 40px), 576px"
            />
            <button
              type="button"
              onClick={handleReset}
              className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center bg-white/90 backdrop-blur-sm text-black transition hover:bg-white"
              aria-label="Quitar foto"
            >
              <LuX className="h-4 w-4" />
            </button>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <span className="text-[11px] font-lealti uppercase tracking-[0.2em] text-black/40">
              Seleccioná una nueva foto
            </span>
          </div>
        )}
      </div>
      {validationMessage && (
        <p className="text-[11px] font-lealti-light text-red-500">
          {validationMessage}
        </p>
      )}
      {renderStickyCTA("Probar de nuevo")}
    </div>
  )

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        onClose={closeDrawer}
        style={{ zIndex: 9999 }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {/* Mobile: full screen | Desktop: right drawer */}
            <div className="pointer-events-none fixed inset-0 sm:inset-y-0 sm:left-auto sm:right-0 sm:flex sm:max-w-xl sm:pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-y-full sm:translate-y-0 sm:translate-x-full"
                enterTo="translate-y-0 sm:translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-y-0 sm:translate-x-0"
                leaveTo="translate-y-full sm:translate-y-0 sm:translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen h-full sm:max-w-xl">
                  <div className="flex h-full flex-col overflow-y-auto bg-white pt-[env(safe-area-inset-top)]">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4 sm:px-8 sm:py-6 pb-0 md:pb-0">
                      <Dialog.Title className="text-xl font-lealti-light text-black">
                        Probador Virtual
                      </Dialog.Title>

                      <button
                        type="button"
                        onClick={closeDrawer}
                        className="p-2 text-black transition-opacity hover:opacity-60"
                        aria-label="Cerrar probador virtual"
                      >
                        <LuX className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 px-5 sm:px-8 pt-0 pb-[calc(env(safe-area-inset-bottom)+6rem)]">
                      <p className="font-lealti-light text-xs md:text-lg text-black">
                        Probá cómo te queda {product?.name ?? "la prenda"}, sin
                        salir de casa con IA.
                      </p>
                      <input
                        key={`gallery-${inputKey}`}
                        ref={fileInputRef}
                        type="file"
                        accept={VIRTUAL_TRY_ON_FILE_ACCEPT_ATTRIBUTE}
                        className="hidden"
                        multiple={false}
                        onChange={handleFileInputChange}
                      />
                      <input
                        key={`camera-${inputKey}`}
                        ref={cameraInputRef}
                        type="file"
                        accept={VIRTUAL_TRY_ON_FILE_ACCEPT_ATTRIBUTE}
                        capture="environment"
                        className="hidden"
                        multiple={false}
                        onChange={handleFileInputChange}
                      />

                      {(status === "idle" || status === "ready") &&
                        renderUploadStep()}
                      {status === "uploading_or_generating" &&
                        renderProcessingStep()}
                      {status === "success" && renderResultStep()}
                      {status === "error_retry" && renderRetryStep()}
                    </div>

                    {/* Footer */}
                    <div className="py-6 text-center text-[10px] font-lealti-light text-black/30">
                      Las imágenes generadas son a modo de referencia,
                      tecnología experimental. Powered by lealti.com
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default VirtualTryOnDrawer
